"use client";

import { c as _c } from "react/compiler-runtime";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy, version } from "pdfjs-dist";
import { isServer } from "@/src/lib/runtime";
import { queryClient } from "@/src/query/query-client";

// Initialize PDF.js worker
if (!isServer) {
  GlobalWorkerOptions.workerSrc = `/pdfjs/${version}/pdf.worker.min.mjs`;
}
export const PDF_QUERY_KEY = "pdf";
export function getPdfQueryKey(url: string) {
  return [PDF_QUERY_KEY, url] as const;
}
export async function fetchPdfDocument(url: string, signal?: AbortSignal) {
  signal?.throwIfAborted();
  const documentLoader = getDocument(url);
  const abortLoading = () => void documentLoader.destroy();
  signal?.addEventListener("abort", abortLoading, {
    once: true
  });
  try {
    return await documentLoader.promise;
  } catch (error) {
    if (!documentLoader.destroyed) {
      await documentLoader.destroy();
    }
    throw error;
  } finally {
    signal?.removeEventListener("abort", abortLoading);
  }
}
queryClient.getQueryCache().subscribe(event => {
  if (event.type === "removed" && event.query.queryKey[0] === PDF_QUERY_KEY) {
    void (event.query.state.data as PDFDocumentProxy | undefined)?.destroy();
  }
});
export function getPdfPrefetchQueryOptions(url: string) {
  return queryOptions({
    queryKey: getPdfQueryKey(url),
    queryFn: ({
      signal
    }) => fetchPdfDocument(url, signal),
    staleTime: Infinity,
    gcTime: 15 * 60 * 1000
  });
}
export function usePdf(url) {
  const $ = _c(8);
  let t0;
  if ($[0] !== url) {
    t0 = getPdfQueryKey(url);
    $[0] = url;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== url) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return fetchPdfDocument(url, signal);
    };
    $[2] = url;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!url;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}